import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { PageHeader, Card, Stat } from "@/components/UI";
import { Plant, CurrencyCircleDollar, Basket, Handshake } from "@phosphor-icons/react";
import { fmt } from "./fields/constants";
import { OverviewTab } from "./fields/OverviewTab";
import { ExpensesTab } from "./fields/ExpensesTab";
import { HarvestsTab } from "./fields/HarvestsTab";
import { SalesTab } from "./fields/SalesTab";
import { AreaCostAnalysis } from "./fields/AreaCostAnalysis";

const TABS = [
  { id: "overview", label: "Genel", icon: Plant },
  { id: "expenses", label: "Giderler", icon: CurrencyCircleDollar },
  { id: "harvests", label: "Hasat", icon: Basket },
  { id: "sales", label: "Satış", icon: Handshake },
];

export default function Fields() {
  const [tab, setTab] = useState("overview");
  const [crops, setCrops] = useState([]);
  const [summary, setSummary] = useState(null);
  const [expenses, setExpenses] = useState([]);
  const [harvests, setHarvests] = useState([]);
  const [sales, setSales] = useState([]);
  const [categoriesList, setCategoriesList] = useState(["sulama", "ilaç", "gübre", "tohum", "tarla işleme", "biçim", "işçilik", "yakıt", "diğer"]);

  const load = () => {
    api.get("/fields/crops").then(r => setCrops(r.data)).catch(() => {});
    api.get("/fields/summary").then(r => { setSummary(r.data); if (r.data?.categories_list) setCategoriesList(r.data.categories_list); }).catch(() => {});
    api.get("/fields/expenses").then(r => setExpenses(r.data)).catch(() => {});
    api.get("/fields/harvests").then(r => setHarvests(r.data)).catch(() => {});
    api.get("/fields/sales").then(r => setSales(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  return (
    <div data-testid="fields-page">
      <PageHeader
        title="Tarla / Kaba Yem Üretimi"
        subtitle="Ürün ekle, gider gir, hasat kaydet. Giderler otomatik Finans'a yansır; hasat üretim değeri olarak burada tutulur."
      />

      {/* Global KPI'lar */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <Stat testId="fields-stat-crops" label="Ürün Sayısı" value={crops.length} tone="accent" />
        <Stat testId="fields-stat-expense" label="Toplam Gider" value={fmt(summary?.total_expense)} unit="₺" tone="warn" />
        <Stat testId="fields-stat-value" label="Toplam Hasat Değeri" value={fmt(summary?.total_harvest_value)} unit="₺" tone="ok" />
        <Stat testId="fields-stat-net" label="Net (Değer − Gider)" value={fmt(summary?.net)} unit="₺" tone={Number(summary?.net || 0) >= 0 ? "ok" : "danger"} />
      </div>

      {/* ÜST BÖLÜM: Ürün başı gider kalemleri (canlı) */}
      {crops.length > 0 && (
        <Card className="mb-6" data-testid="fields-expense-breakdown">
          <div className="flex items-center gap-2 mb-3">
            <CurrencyCircleDollar size={18} weight="duotone" className="text-[#C45B45]" />
            <h3 className="heading text-lg">Ürün Bazlı Gider Toplamları</h3>
            <span className="text-xs text-[#8a938c]">— gider ekledikçe canlı olarak artar</span>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-3">
            {crops.map(c => (
              <div key={c.id} data-testid={`fields-expense-card-${c.id}`}
                   className="border border-[#E5E2DC] rounded-md p-3 bg-white">
                <div className="text-xs uppercase tracking-wider text-[#8a938c] truncate">{c.name}</div>
                <div className="mt-1 flex items-baseline gap-1">
                  <span className="text-2xl font-bold text-[#C45B45] tabnum">{fmt(c.total_expense)}</span>
                  <span className="text-sm text-[#8a938c]">₺</span>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Sekmeler */}
      <div className="flex flex-wrap gap-1 mb-6 border-b border-[#E5E2DC]">
        {TABS.map(t => (
          <button key={t.id} data-testid={`fields-tab-${t.id}`} onClick={() => setTab(t.id)}
            className={`px-4 py-2.5 text-sm font-semibold border-b-2 -mb-px flex items-center gap-1.5 ${tab === t.id ? "border-[#1A1D1A] text-[#1A1D1A]" : "border-transparent text-[#6b746c] hover:text-[#1A1D1A]"}`}>
            <t.icon size={16} weight="duotone" /> {t.label}
          </button>
        ))}
      </div>

      {tab === "overview" && <OverviewTab crops={crops} reload={load} />}
      {tab === "expenses" && <ExpensesTab crops={crops} expenses={expenses} categoriesList={categoriesList} reload={load} />}
      {tab === "harvests" && <HarvestsTab crops={crops} harvests={harvests} reload={load} />}
      {tab === "sales" && <SalesTab crops={crops} sales={sales} reload={load} />}

      {/* ALT BÖLÜM: Ürün başı hasat değerleri */}
      {crops.length > 0 && (
        <Card className="mt-6" data-testid="fields-harvest-breakdown">
          <div className="flex items-center gap-2 mb-3">
            <Basket size={18} weight="duotone" className="text-[#2D4A36]" />
            <h3 className="heading text-lg">Ürün Bazlı Hasat & Satış</h3>
            <span className="text-xs text-[#8a938c]">— hasat/satış girdikçe canlı olarak artar</span>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-3">
            {crops.map(c => (
              <div key={c.id} data-testid={`fields-harvest-card-${c.id}`}
                   className="border border-[#E5E2DC] rounded-md p-3 bg-white">
                <div className="text-xs uppercase tracking-wider text-[#8a938c] truncate">{c.name}</div>
                <div className="mt-1 flex items-baseline gap-1">
                  <span className="text-2xl font-bold text-[#2D4A36] tabnum">{fmt(c.total_harvest_value)}</span>
                  <span className="text-sm text-[#8a938c]">₺</span>
                </div>
                <div className="text-xs text-[#8a938c] mt-1 tabnum">
                  Hasat: {fmt(c.total_harvest_amount)} {c.last_unit || "birim"}
                </div>
                {Number(c.total_sold_amount || 0) > 0 && (
                  <div className="mt-2 pt-2 border-t border-dashed border-[#E5E2DC]">
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-[#8a938c]">Satılan</span>
                      <span className="tabnum font-semibold text-[#C45B45]">
                        {fmt(c.total_sold_amount)} {c.last_unit || "birim"}
                      </span>
                    </div>
                    <div className="flex items-center justify-between text-xs mt-0.5">
                      <span className="text-[#8a938c]">Gelir</span>
                      <span className="tabnum font-semibold text-[#3E5B36]">
                        {fmt(c.total_sold_value)} ₺
                      </span>
                    </div>
                    <div className="flex items-center justify-between text-xs mt-0.5">
                      <span className="text-[#8a938c]">Kalan</span>
                      <span className="tabnum font-semibold">
                        {fmt(c.remaining_amount)} {c.last_unit || "birim"}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* ALAN BAZLI MALİYET ANALİZİ */}
      <AreaCostAnalysis crops={crops} />
    </div>
  );
}
